(() => {
  // Demo money(): "$15", "$15.50" (no trailing .00).
  const money = (amount, currency) => new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: (Number(amount) || 0) % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  }).format((Number(amount) || 0) / 100);
  const escapeHtml = (value) => String(value || '').replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[character]));

  const initSetBuilder = (root) => {
    if (root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';

    const maximum = Number(root.dataset.maxPieces) || 4;
    const tinDiscount = Number(root.dataset.tinDiscount) || 0;
    const currency = root.dataset.currency || 'USD';
    const selection = new Map();
    const countNode = root.querySelector('[data-set-count]');
    const progressNode = root.querySelector('[data-set-progress]');
    const linesNode = root.querySelector('[data-set-lines]');
    const subtotalNode = root.querySelector('[data-set-subtotal]');
    const savingsNode = root.querySelector('[data-set-savings]');
    const totalNode = root.querySelector('[data-set-total]');
    const statusNode = root.querySelector('[data-set-status]');
    const submitNode = root.querySelector('[data-set-submit]');
    const cards = [...root.querySelectorAll('[data-set-card]')];
    // Fix 9: phone bottom bar ("N of 4 pieces · $X · save $Y") and its expandable sheet.
    const summaryNode = root.querySelector('[data-set-summary]');
    const sheetToggle = root.querySelector('[data-set-sheet-toggle]');
    const barSummaryNode = root.querySelector('[data-set-bar-summary]');
    const barLabelNode = root.querySelector('[data-set-bar-label]');
    const barProgressNode = root.querySelector('[data-set-bar-progress]');
    const setSheetOpen = (open) => {
      if (!summaryNode || !sheetToggle) return;
      // Review 9: closing while focus is inside the sheet (now display:none) would drop focus to <body>.
      if (!open && summaryNode.classList.contains('is-open') && summaryNode.contains(document.activeElement) && !sheetToggle.contains(document.activeElement) && !submitNode.contains(document.activeElement)) sheetToggle.focus();
      summaryNode.classList.toggle('is-open', open);
      root.classList.toggle('is-sheet-open', open);
      sheetToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (barLabelNode) barLabelNode.textContent = open ? 'Hide set' : 'View set';
    };
    let formatFilter = 'all';
    let useFilter = 'all';

    const totalPieces = () => [...selection.values()].reduce((sum, item) => sum + item.quantity, 0);
    const totalTins = () => [...selection.values()].reduce((sum, item) => sum + (item.format === 'tin' ? item.quantity : 0), 0);

    const filterCards = () => {
      cards.forEach((card) => {
        const formats = (card.dataset.formats || '').split(/\s+/);
        const uses = (card.dataset.uses || '').split(/\s+/);
        const formatMatch = formatFilter === 'all' || formats.includes(formatFilter);
        const useMatch = useFilter === 'all' || uses.includes(useFilter);
        card.hidden = !(formatMatch && useMatch);
        card.querySelectorAll('[data-set-row]').forEach((row) => {
          row.hidden = formatFilter !== 'all' && row.dataset.format !== formatFilter;
        });
      });
    };

    const render = () => {
      const pieces = totalPieces();
      const tins = totalTins();
      const subtotal = [...selection.values()].reduce((sum, item) => sum + item.price * item.quantity, 0);
      const isValid = pieces >= 2 && pieces <= maximum && tins >= 1;
      // Demo calc(): the $2 per tin shows once the pieces make a set (2 to 4, at least one tin).
      const savings = isValid ? tins * tinDiscount : 0;

      countNode.textContent = pieces;
      progressNode.style.width = `${Math.min(100, (pieces / maximum) * 100)}%`;
      if (barProgressNode) barProgressNode.style.width = `${Math.min(100, (pieces / maximum) * 100)}%`;
      if (barSummaryNode) {
        barSummaryNode.textContent = [
          `${pieces} of ${maximum} ${root.dataset.piecesLabel || 'pieces'}`,
          pieces ? money(Math.max(0, subtotal - savings), currency) : '',
          savings ? `save ${money(savings, currency)}` : ''
        ].filter(Boolean).join(' · ');
      }
      delete statusNode.dataset.tone;
      subtotalNode.textContent = money(subtotal, currency);
      savingsNode.textContent = `−${money(savings, currency)}`;
      const savingsRow = root.querySelector('[data-set-savings-row]');
      if (savingsRow) savingsRow.hidden = !savings;
      root.querySelectorAll('[data-set-ladder] > div').forEach((step) => step.classList.toggle('on', isValid));
      totalNode.textContent = money(Math.max(0, subtotal - savings), currency);
      submitNode.disabled = !isValid;
      // Demo button label: "Add set to bag · $X", "Pick at least two pieces" or "Include a tin".
      if (!submitNode.classList.contains('is-loading')) {
        submitNode.textContent = isValid
          ? `${root.dataset.submitLabel || 'Add set to bag'} · ${money(Math.max(0, subtotal - savings), currency)}`
          : pieces < 2 ? (root.dataset.submitPickTwo || 'Pick at least two pieces') : (root.dataset.submitIncludeTin || 'Include a tin');
      }
      // Shipping row (display only): the store's free US shipping threshold on the set price.
      const shippingNode = root.querySelector('[data-set-shipping]');
      if (shippingNode) {
        const threshold = Number(root.dataset.freeShipping) || 10000;
        const net = Math.max(0, subtotal - savings);
        const freeFrom = root.dataset.shippingFreeFrom || 'Free from $100';
        shippingNode.textContent = !pieces ? freeFrom : net >= threshold ? (root.dataset.shippingFree || 'Free') : `${freeFrom} · ${money(threshold - net, currency)} to go`;
      }

      if (pieces < 2) statusNode.textContent = root.dataset.messageChooseTwo || 'Choose at least 2 pieces.';
      else if (tins < 1) statusNode.textContent = root.dataset.messageChooseTin || 'Include at least one tin.';
      else if (pieces === maximum) statusNode.textContent = root.dataset.messageReadyFull || 'Your set is full and ready.';
      else statusNode.textContent = root.dataset.messageReady || 'Your set is ready. You may add more.';

      if (!selection.size) {
        linesNode.innerHTML = `<p class="saintcha-set__empty">${escapeHtml(root.dataset.empty || '')}</p>`;
      } else {
        // Demo set line: "Name · size", "qty × $price[ · $2 tin saving]", line total, then a "−" chip.
        const cut = isValid ? tinDiscount : 0;
        linesNode.innerHTML = [...selection.values()].map((item) => `
          <div class="saintcha-set__line set-line">
            <span><span class="set-line__name">${escapeHtml(item.title)} · ${escapeHtml(item.variant)}</span><span class="small soft">${item.quantity} × ${money(item.price, currency)}${cut && item.format === 'tin' ? ' · $2 tin saving' : ''}</span></span>
            <span>${money((item.price - (item.format === 'tin' ? cut : 0)) * item.quantity, currency)}</span>
            <button class="chip set-line__minus" type="button" data-set-minus="${item.id}" aria-label="Remove one ${escapeHtml(item.title)}">−</button>
          </div>
        `).join('');
      }

      root.querySelectorAll('[data-set-add]').forEach((button) => {
        const item = selection.get(button.dataset.id);
        const canAdd = pieces < maximum && !(item && item.quantity >= item.max);
        button.classList.toggle('is-selected', Boolean(item));
        button.disabled = !canAdd;
        // Demo chip: "+ Add", or "Added" once this format is in the set and cannot take another.
        button.textContent = canAdd || !item ? '+ Add' : 'Added';
        const row = button.closest('[data-set-row]');
        const meta = row && row.querySelector('[data-set-row-meta]');
        if (meta) meta.textContent = `${meta.dataset.setRowMeta}${item ? `${meta.dataset.setRowMeta ? ' · ' : ''}${item.quantity} in set` : ''}`;
      });
      cards.forEach((card) => card.classList.toggle('on', [...card.querySelectorAll('[data-set-add]')].some((button) => selection.has(button.dataset.id))));
    };

    root.querySelectorAll('[data-set-format-filter] [data-set-filter]').forEach((button) => {
      button.addEventListener('click', () => {
        formatFilter = button.dataset.setFilter;
        root.querySelectorAll('[data-set-format-filter] [data-set-filter]').forEach((peer) => {
          peer.classList.toggle('is-active', peer === button);
          peer.classList.toggle('on', peer === button);
          peer.setAttribute('aria-pressed', peer === button ? 'true' : 'false');
        });
        filterCards();
      });
    });

    root.querySelectorAll('[data-set-use-filter] [data-set-filter]').forEach((button) => {
      button.addEventListener('click', () => {
        useFilter = button.dataset.setFilter;
        root.querySelectorAll('[data-set-use-filter] [data-set-filter]').forEach((peer) => {
          peer.classList.toggle('is-active', peer === button);
          peer.classList.toggle('on', peer === button);
          peer.setAttribute('aria-pressed', peer === button ? 'true' : 'false');
        });
        filterCards();
      });
    });

    if (sheetToggle) {
      sheetToggle.addEventListener('click', () => setSheetOpen(!summaryNode.classList.contains('is-open')));
      root.querySelectorAll('[data-set-sheet-close]').forEach((node) => node.addEventListener('click', () => setSheetOpen(false)));
      // Review 9: listen on document, not root. After a "−" re-renders the lines, focus can sit on <body>,
      // and Escape on root never fired.
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && summaryNode.classList.contains('is-open')) {
          setSheetOpen(false);
          sheetToggle.focus();
        }
      });
    }

    root.addEventListener('click', (event) => {
      const add = event.target.closest('[data-set-add]');
      const plus = event.target.closest('[data-set-plus]');
      const minus = event.target.closest('[data-set-minus]');
      if (add) {
        const id = add.dataset.id;
        const current = selection.get(id);
        if (totalPieces() >= maximum || (current && current.quantity >= current.max)) return;
        if (current) current.quantity += 1;
        else selection.set(id, {
          id,
          title: add.dataset.title,
          variant: add.dataset.variant,
          house: add.dataset.house,
          format: add.dataset.format,
          price: Number(add.dataset.price),
          max: Number(add.dataset.max) || 5,
          image: add.dataset.image,
          quantity: 1
        });
        render();
      }
      if (plus) {
        const item = selection.get(plus.dataset.setPlus);
        if (item && totalPieces() < maximum && item.quantity < item.max) item.quantity += 1;
        render();
      }
      if (minus) {
        const item = selection.get(minus.dataset.setMinus);
        if (!item) return;
        const hadFocus = document.activeElement === minus;
        item.quantity -= 1;
        if (item.quantity <= 0) selection.delete(item.id);
        render();
        // Review 9: render() rebuilds the lines, so keep keyboard focus on this line's new "−" (or, when the
        // line is gone and the phone sheet is open, on the sheet toggle). Focus only; selection is unchanged.
        if (hadFocus) {
          const again = root.querySelector(`[data-set-minus="${item.id}"]`);
          if (again) again.focus();
          else if (sheetToggle && summaryNode && summaryNode.classList.contains('is-open')) sheetToggle.focus();
        }
      }
    });

    submitNode.addEventListener('click', async () => {
      if (submitNode.disabled) return;
      submitNode.disabled = true;
      submitNode.classList.add('is-loading');
      try {
        const rootPath = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
        const addSet = async () => {
          const cartResponse = await fetch(`${rootPath}cart.js`, {
            cache: 'no-store',
            headers: { Accept: 'application/json' }
          });
          if (!cartResponse.ok) throw new Error('Unable to check your bag. Please try again.');
          const cart = await cartResponse.json();
          // Cumulative limit, checked per piece before anything is added: what is already in the bag
          // (all lines, other sets included) plus this set may not exceed the piece's limit (data-max).
          // The whole set is blocked rather than partly added.
          const inBag = (variantId) => cart.items.reduce((sum, line) => sum + (String(line.variant_id) === String(variantId) ? Number(line.quantity) || 0 : 0), 0);
          const overLimit = [...selection.values()].filter((item) => inBag(item.id) + item.quantity > item.max);
          if (overLimit.length) {
            const detail = overLimit.map((item) => {
              const already = inBag(item.id);
              return `${item.title} ${item.variant}: ${item.max} available and ${already} already in your bag.`;
            }).join(' ');
            throw new Error(`This set can't be added. ${detail} Adjust the set or your bag and try again.`);
          }
          const occupied = new Set(cart.items.map((item) => item.properties && item.properties['_SAINTCHA set group']));
          const group = Array.from({ length: 5 }, (_, index) => `SAINTCHA-SET-${index + 1}`)
            .find((slot) => !occupied.has(slot));
          if (!group) throw new Error('Your bag already has 5 sets. Complete your order or remove a set before adding another.');
          const items = [...selection.values()].map((item) => ({
            id: Number(item.id),
            quantity: item.quantity,
            properties: {
              'SAINTCHA set': 'Build a set',
              'SAINTCHA set format': item.format === 'tin' ? 'Tin' : item.format === 'bag' ? 'Original bag' : 'SAINTCHA-packed refill',
              '_SAINTCHA set group': group
            }
          }));
          const response = await fetch(`${rootPath}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items })
          });
          if (!response.ok) {
            // Shopify's 422 description names the item and the quantity it allows (shipping stock).
            const error = await response.json().catch(() => ({}));
            const detail = typeof error.description === 'string' ? error.description : '';
            throw new Error([root.dataset.addError || 'Unable to add this set.', detail].filter(Boolean).join(' '));
          }
        };
        if (navigator.locks && navigator.locks.request) {
          await navigator.locks.request('saintcha-add-set', addSet);
        } else {
          await addSet();
        }
        // Added: fly the first piece's pack photo to the Bag count and open the drawer
        // (falls back to /cart when the drawer is not available).
        const firstImage = ([...selection.values()].find((item) => item.image) || {}).image || '';
        if (window.SaintchaBag && typeof window.SaintchaBag.added === 'function') {
          const flyImage = firstImage;
          selection.clear();
          submitNode.classList.remove('is-loading');
          render();
          statusNode.textContent = root.dataset.messageAdded || 'Your set is in your bag.';
          setSheetOpen(false);
          window.SaintchaBag.added({ source: submitNode, image: flyImage });
        } else {
          window.location.assign(`${rootPath}cart`);
        }
      } catch (error) {
        statusNode.textContent = error.message;
        statusNode.dataset.tone = 'alert';
        submitNode.disabled = false;
        submitNode.classList.remove('is-loading');
      }
    });

    // ?variants= prefill (home "Review this set", PDP trio, refill links) and the "Start from a set we love"
    // presets on this page share one path: clear the set, then add each listed piece through its "+ Add" chip.
    const parseIds = (value) => String(value || '').split(',').filter((id) => /^\d+$/.test(id)).slice(0, maximum);
    const prefill = (ids) => {
      selection.clear();
      render();
      ids.forEach((id) => {
        const button = [...root.querySelectorAll('[data-set-add]')].find((candidate) => candidate.dataset.id === id);
        if (button && !button.disabled) button.click();
      });
      filterCards();
      render();
      if (ids.length && [...selection.values()].reduce((sum, item) => sum + item.quantity, 0) < ids.length) {
        statusNode.textContent = 'Some items in this set are unavailable. Please review your selection.';
        statusNode.dataset.tone = 'alert';
      }
    };
    root.saintchaSetPrefill = prefill;
    root.querySelectorAll('[data-set-preset]').forEach((preset) => {
      preset.addEventListener('click', () => {
        const ids = parseIds(preset.dataset.setPreset);
        prefill(ids);
        // Keep the URL in step (same as arriving with ?variants=), without a reload.
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variants', ids.join(','));
          window.history.replaceState(window.history.state, '', url);
        } catch (error) { /* ignore */ }
        const target = root.querySelector('.saintcha-set__filters') || root.querySelector('[data-set-layout]');
        if (target && typeof target.scrollIntoView === 'function') {
          const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        }
      });
    });
    const presetIds = parseIds(new URLSearchParams(window.location.search).get('variants'));
    if (presetIds.length) prefill(presetIds);
    else { filterCards(); render(); }
  };

  const init = () => document.querySelectorAll('[data-set-builder]').forEach(initSetBuilder);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
